<template>
  <div ref="root">
    <transition :name="transition">
      <div
        v-show="isActive"
        class="ak-toast"
        :class="{ 'ak-toast--inverse': inverse }"
        :role="actionText ? 'alertdialog' : 'alert'"
      >
        <div class="ak-toast__body" v-html="message" />
        <div
          v-if="actionText"
          class="ak-toast__action"
          :class="type"
          @click="action"
        >
          <ak-button
            :class="'color-' + type"
            :color="inverse ? 'transparent' : 'transparent-light'"
            size="small"
          >
            {{ actionText }}
          </ak-button>
        </div>
        <ak-close
          v-if="actionDismiss"
          class="ak-toast__dismiss"
          @click="dismiss"
        ></ak-close>
      </div>
    </transition>
  </div>
</template>

<script>
import config from '../../utils/config'
import Button from '../button/Button.vue'
import Close from '../close/Close.vue'
import { defineComponent, ref } from 'vue'
import {
  snackbarProps,
  noticeProps,
  noticeMixin,
} from '../../mixins/noticeMixin'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-snackbar />
   */
  name: 'AkSnackbar',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Button.name]: Button,
    [Close.name]: Close,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...noticeProps,
    ...snackbarProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['close'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const root = ref(null)
    const newDuration = ref(props.duration || config.defaultSnackbarDuration)

    const mixin = noticeMixin({
      props,
      root,
      emit,
    })

    /**
     * @description Click listener. Call action prop before closing
     * (from Mixin).
     */
    const action = () => {
      props.onAction()
      mixin.close()
    }

    /**
     * @description Dismiss
     */
    const dismiss = () => {
      mixin.close()
    }

    return {
      ...mixin,
      root,
      props,
      newDuration,
      action,
      dismiss,
    }
  },
})
</script>
